import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TravelEntry } from '@/types/unified-flow';
import { COUNTRIES } from '@/data/questions';
import { CountryCombobox } from './CountryCombobox';
import { getUiTranslations } from '@/lib/ui-translations';
import { getDateFnsLocale, formatDateLocalized, formatDateShortLocalized } from '@/lib/date-locale';
import { getCountryName as getLocalizedCountryName, getLocalizedOptionLabel } from '@/lib/option-labels';

interface TravelHistoryInputProps {
  entries: TravelEntry[];
  hasNoTravel: boolean;
  onEntriesChange: (entries: TravelEntry[]) => void;
  onNoTravelChange: (hasNoTravel: boolean) => void;
  language?: string;
}

export function TravelHistoryInput({
  entries,
  hasNoTravel,
  onEntriesChange,
  onNoTravelChange,
  language = 'fr',
}: TravelHistoryInputProps) {
  const t = getUiTranslations(language);
  
  const generateId = () => Math.random().toString(36).substring(2, 9);

  const addEntry = () => {
    const newEntry: TravelEntry = {
      id: generateId(),
      country: '',
      startDate: null,
      endDate: null,
    };
    onEntriesChange([...entries, newEntry]);
    onNoTravelChange(false);
  };

  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  const removeEntry = (id: string) => {
    onEntriesChange(entries.filter(e => e.id !== id));
    setConfirmingDelete(null);
  };

  const updateEntry = (id: string, field: keyof TravelEntry, value: any) => {
    onEntriesChange(entries.map(e => 
      e.id === id ? { ...e, [field]: value } : e
    ));
  };

  const handleNoTravelChange = (checked: boolean) => {
    onNoTravelChange(checked);
    if (checked) {
      onEntriesChange([]);
    }
  };

  const getCountryName = (value: string): string => {
    // Support both name (new) and code (old) formats
    const country = COUNTRIES.find(c => c.name === value) || COUNTRIES.find(c => c.code === value);
    return country ? getLocalizedOptionLabel(country, language) : value;
  };

  return (
    <div className="space-y-4">
      {/* No travel checkbox */}
      <div className="p-4 bg-secondary/50 rounded-xl">
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={hasNoTravel}
            onCheckedChange={(checked) => handleNoTravelChange(checked as boolean)}
          />
          <span className="text-sm text-foreground">
            {t.noTravelHistoryCheckbox}
          </span>
        </label>
      </div>

      {/* Travel entries */}
      {!hasNoTravel && (
        <>
          <div className="space-y-4">
            {entries.map((entry, index) => (
              <div 
                key={entry.id} 
                className="p-4 bg-secondary/30 rounded-xl border border-border/50 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">
                    {t.tripHeader(index + 1)}
                  </span>
                  {confirmingDelete === entry.id ? (
                    <button
                      onClick={() => removeEntry(entry.id)}
                      onBlur={() => setConfirmingDelete(null)}
                      autoFocus
                      className="px-2 py-1 bg-destructive text-destructive-foreground text-xs font-medium rounded-lg transition-colors"
                      aria-label={t.confirmDeleteEntryAria(index + 1)}
                    >
                      {t.deleteConfirm}
                    </button>
                  ) : (
                    <button
                      onClick={() => setConfirmingDelete(entry.id)}
                      className="p-1.5 hover:bg-destructive/10 rounded-lg transition-colors"
                      aria-label={t.removeEntryAria(index + 1)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                  )}
                </div>

                {/* Country select */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1.5 block">
                    {t.countryVisited}
                  </label>
                  <CountryCombobox
                    value={entry.country}
                    onValueChange={(value) => updateEntry(entry.id, 'country', value)}
                    language={language}
                  />
                </div>

                {/* Date range */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Start date */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1.5 block">
                      {t.tripStartDateLabel}
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full h-12 justify-start text-left font-normal",
                            !entry.startDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          <span className="truncate">
                            {entry.startDate ? formatDateLocalized(entry.startDate, language) : t.tripStartDateShort}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-card" align="start">
                        <Calendar
                          mode="single"
                          selected={entry.startDate || undefined}
                          onSelect={(date) => updateEntry(entry.id, 'startDate', date)}
                          disabled={(date) => date > new Date()}
                          initialFocus
                          captionLayout="dropdown-buttons"
                          fromYear={new Date().getFullYear() - 5}
                          toYear={new Date().getFullYear()}
                          className={cn("p-3 pointer-events-auto")}
                          locale={getDateFnsLocale(language)}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* End date */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1.5 block">
                      {t.tripEndDateLabel}
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full h-12 justify-start text-left font-normal",
                            !entry.endDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          <span className="truncate">
                            {entry.endDate ? formatDateLocalized(entry.endDate, language) : t.tripEndDateShort}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-card" align="start">
                        <Calendar
                          mode="single"
                          selected={entry.endDate || undefined}
                          onSelect={(date) => updateEntry(entry.id, 'endDate', date)}
                          disabled={(date) =>
                            date > new Date() ||
                            (entry.startDate ? date < entry.startDate : false)
                          }
                          initialFocus
                          captionLayout="dropdown-buttons"
                          fromYear={new Date().getFullYear() - 5}
                          toYear={new Date().getFullYear()}
                          className={cn("p-3 pointer-events-auto")}
                          locale={getDateFnsLocale(language)}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Entry summary */}
                {entry.country && entry.startDate && entry.endDate && (
                  <div className="text-sm text-muted-foreground bg-background/50 rounded-lg p-2 text-center">
                    {getCountryName(entry.country)} — {t.tripDateRange(formatDateLocalized(entry.startDate, language), formatDateLocalized(entry.endDate, language))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add button */}
          <button
            onClick={addEntry}
            className="w-full p-4 border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5 text-primary" />
            <span className="font-medium text-foreground">
              {entries.length === 0 ? t.addTravelEntry : t.addAnotherTravelEntry}
            </span>
          </button>
        </>
      )}
    </div>
  );
}

// Helper to format travel history for display
export function formatTravelHistoryForReview(
  entries: TravelEntry[],
  hasNoTravel: boolean,
  language: string = 'fr',
): string {
  const t = getUiTranslations(language);
  if (hasNoTravel) {
    return t.noTripsReview;
  }

  if (entries.length === 0) {
    return '';
  }

  return entries
    .filter(e => e.country && e.startDate && e.endDate)
    .map(e => {
      // Support both name (new) and code (old) formats; localize whichever form we land on
      const matched = COUNTRIES.find(c => c.name === e.country) || COUNTRIES.find(c => c.code === e.country);
      const country = matched
        ? getLocalizedOptionLabel(matched, language)
        : getLocalizedCountryName(e.country, language);
      return `${country} (${formatDateShortLocalized(e.startDate!, language)} - ${formatDateShortLocalized(e.endDate!, language)})`;
    })
    .join(', ');
}
